import { useEffect, useMemo, useRef, useState } from "react";
import { type NutrientTableRow } from "../components/NutrientTable";
import { type NutrientGroup } from "../utils/nutrientPlots";
import { calculateNutrientAmount } from "../../../shared/utils/nutrientCalculation";
import { calculateTotalAmount } from "../../../shared/utils/areaCalculation";
import { requestNutrientAutoAssignment } from "../services/nutrientAutoAssignment";
import {
  type ApplyRateError,
  type ProtocolNutrientDetails,
  validateApplyRates,
} from "../utils/nutrientValidation";

export type NutrientLayout = "plots" | "stripes";

export interface NutrientOption {
  label: string;
  value: string;
  nitrogenRatio: number;
  nutrients?: Array<{ nutrient_type: string; percentage: number }>;
}

export interface StandardRate {
  nutrientValue?: string;
  rate: number | null;
}

export interface NutrientInitialData {
  trialId?: string;
  protocolId?: string;
  draftStorageKey?: string;
  fertilisers: StandardFertiliser[];
  applyByPlot: Record<string, number | null>;
  productKgHaByPlot?: Record<string, number>;
}

const EMPTY_STANDARD_RATE: StandardRate = { rate: null };
const EMPTY_OPTIONS: NutrientOption[] = [];

export interface StandardFertiliser {
  id: string;
  nutrientValue?: string;
  rate: number | null;
  // Rows added via "More Nutrients" start touched so their errors show at once.
  touched: boolean;
}

export interface NutrientState {
  nutrientCode?: string;
  layout: NutrientLayout;
  groups: NutrientGroup[];
  fertilisers: StandardFertiliser[];
  totalRate: number;
  levels: number;
  requiredLevels?: number;
  totalPlots: number;
  assigned: number;
  isFullyAssigned: boolean;
  // Every plot has a rate, the standard rate is valid and the apply rates pass
  // the protocol's rules; this is what completes the tab.
  isComplete: boolean;
  isDirty: boolean;
  canReset: boolean;
  canAutoAssign: boolean;
  isAutoAssigning: boolean;
  autoAssignmentFailed: boolean;
  closeAutoAssignmentError: () => void;
  rowsByGroup: Record<string, NutrientTableRow[]>;
  applyByPlot: Record<string, number | null>;
  applyErrorsByPlot: Record<string, ApplyRateError[]>;
  highlightedPlotIds: string[];
  expandedGroups: Record<string, boolean>;
  nutrientOptionsFor: (fertiliserId: string) => NutrientOption[];
  setFertiliserNutrient: (fertiliserId: string, value: string) => void;
  setFertiliserRate: (fertiliserId: string, value: number | null) => void;
  addFertiliser: () => void;
  removeFertiliser: (fertiliserId: string) => void;
  setApply: (rowId: string, value: number | null) => void;
  toggleGroup: (groupId: string) => void;
  togglePlotHighlight: (plotId: string) => void;
  autoAssign: () => Promise<void>;
  /** Clears standard fertilisers and assignments in the draft without saving. */
  reset: () => void;
}

const FIRST_FERTILISER_ID = "fertiliser-1";

const initialFertilisers = (
  standardRate: StandardRate
): StandardFertiliser[] => [
  {
    id: FIRST_FERTILISER_ID,
    nutrientValue: standardRate.nutrientValue,
    rate: standardRate.rate,
    touched: false,
  },
];

const isFertiliserValid = (fertiliser: StandardFertiliser): boolean =>
  Boolean(fertiliser.nutrientValue?.trim()) &&
  fertiliser.rate !== null &&
  fertiliser.rate > 0;

const readNutrientDraft = (
  storageKey: string | undefined,
  identity: string,
  saved: NutrientInitialData | undefined
): NutrientInitialData | undefined => {
  if (!storageKey || !saved) return saved;
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return saved;
    const draft = JSON.parse(stored);
    if (
      draft.identity !== identity ||
      !Array.isArray(draft.fertilisers) ||
      !draft.fertilisers.every(
        (fertiliser: StandardFertiliser) =>
          fertiliser &&
          typeof fertiliser.id === "string" &&
          (fertiliser.nutrientValue === undefined ||
            typeof fertiliser.nutrientValue === "string") &&
          (fertiliser.rate === null || Number.isFinite(fertiliser.rate))
      ) ||
      !draft.applyByPlot ||
      typeof draft.applyByPlot !== "object" ||
      Array.isArray(draft.applyByPlot) ||
      !Object.values(draft.applyByPlot).every(
        (rate) => rate === null || Number.isFinite(rate)
      )
    )
      return saved;
    return {
      ...saved,
      fertilisers: draft.fertilisers,
      applyByPlot: draft.applyByPlot,
    };
  } catch (error) {
    console.error("Failed to restore the nutrient draft:", error);
    return saved;
  }
};

export const useNutrientState = (
  layout: NutrientLayout,
  groups: NutrientGroup[],
  standardRate: StandardRate = EMPTY_STANDARD_RATE,
  protocolNutrients?: ProtocolNutrientDetails | null,
  options: NutrientOption[] = EMPTY_OPTIONS,
  initialData?: NutrientInitialData,
  nutrientCode = "N"
): NutrientState => {
  const draftStorageKey = initialData?.draftStorageKey;
  const savedSignature = JSON.stringify(initialData);
  const draftIdentity = JSON.stringify([
    draftStorageKey,
    savedSignature,
    layout,
    groups.map((group) => group.plots.map((plot) => [plot.id, plot.areaHa])),
    protocolNutrients,
    nutrientCode,
  ]);
  const [initialSnapshot] = useState(() =>
    readNutrientDraft(draftStorageKey, draftIdentity, initialData)
  );
  const [hydratedIdentity, setHydratedIdentity] = useState(draftIdentity);
  const [fertilisers, setFertilisers] = useState(
    () => initialSnapshot?.fertilisers ?? initialFertilisers(standardRate)
  );
  const nextFertiliserId = useRef(2);
  const [applyByRow, setApplyByRow] = useState<Record<string, number | null>>(
    initialSnapshot?.applyByPlot ?? {}
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );
  const [levels, setLevels] = useState(0);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [autoAssignmentFailed, setAutoAssignmentFailed] = useState(false);
  const autoAssigning = useRef(false);
  const [highlightedPlotIds, setHighlightedPlotIds] = useState<string[]>(() =>
    groups.flatMap((group) => group.plots.map((plot) => plot.id))
  );
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;

  useEffect(() => {
    const saved = readNutrientDraft(
      draftStorageKey,
      draftIdentity,
      initialDataRef.current
    );
    if (saved) {
      setFertilisers(saved.fertilisers);
      setApplyByRow(saved.applyByPlot);
      setHighlightedPlotIds(
        groupsRef.current.flatMap((group) => group.plots.map((plot) => plot.id))
      );
      setAutoAssignmentFailed(false);
      nextFertiliserId.current = Math.max(
        2,
        ...saved.fertilisers.map((fertiliser) => {
          const suffix = /^fertiliser-(\d+)$/.exec(fertiliser.id)?.[1];
          return suffix ? Number(suffix) + 1 : 2;
        })
      );
    }
    setHydratedIdentity(draftIdentity);
  }, [draftStorageKey, draftIdentity]);

  useEffect(() => {
    if (!draftStorageKey || hydratedIdentity !== draftIdentity) return;
    try {
      const saved = initialDataRef.current;
      if (
        JSON.stringify(fertilisers) === JSON.stringify(saved?.fertilisers) &&
        JSON.stringify(applyByRow) === JSON.stringify(saved?.applyByPlot)
      ) {
        localStorage.removeItem(draftStorageKey);
      } else {
        localStorage.setItem(
          draftStorageKey,
          JSON.stringify({
            identity: draftIdentity,
            fertilisers,
            applyByPlot: applyByRow,
          })
        );
      }
    } catch (error) {
      console.error("Failed to persist the nutrient draft:", error);
    }
  }, [
    draftStorageKey,
    draftIdentity,
    hydratedIdentity,
    fertilisers,
    applyByRow,
  ]);

  const totalRate = fertilisers.reduce(
    (sum, fertiliser) => sum + (fertiliser.rate ?? 0),
    0
  );
  const standardFertilisers = useMemo(
    () =>
      fertilisers.map((fertiliser) => {
        const option = options.find(
          (nutrient) =>
            nutrient.value === fertiliser.nutrientValue ||
            nutrient.label === fertiliser.nutrientValue
        );
        return {
          rate: fertiliser.rate ?? 0,
          nutrients: option?.nutrients ?? [
            {
              nutrient_type: nutrientCode,
              percentage: (option?.nitrogenRatio ?? 0) * 100,
            },
          ],
        };
      }),
    [fertilisers, options, nutrientCode]
  );
  const allPlots = groups.flatMap((group) => group.plots);
  const assigned = allPlots.filter(
    (plot) => (applyByRow[plot.id] ?? null) !== null
  ).length;

  const validation = useMemo(
    () =>
      validateApplyRates(
        groups.flatMap((group) =>
          group.plots.map((plot) => ({
            id: plot.id,
            applyPercent: applyByRow[plot.id] ?? null,
          }))
        ),
        protocolNutrients
      ),
    [groups, applyByRow, protocolNutrients]
  );
  const applyErrorsByPlot = useMemo(() => {
    const byPlot: Record<string, ApplyRateError[]> = {};
    validation.errors.forEach((error) => {
      byPlot[error.plotId] = [...(byPlot[error.plotId] ?? []), error];
    });
    return byPlot;
  }, [validation]);
  const isAllAssigned = allPlots.length > 0 && assigned === allPlots.length;
  const isStandardValid =
    fertilisers.length > 0 && fertilisers.every(isFertiliserValid);
  const hasMissingSavedFertilisers =
    Object.keys(initialData?.productKgHaByPlot ?? {}).length > 0 &&
    !initialData?.fertilisers.some((fertiliser) =>
      Boolean(fertiliser.nutrientValue?.trim())
    );
  const showSavedAmounts = hasMissingSavedFertilisers && !isStandardValid;

  const rowsByGroup = useMemo(
    () =>
      Object.fromEntries(
        groups.map((group) => [
          group.id,
          group.plots.map((plot): NutrientTableRow => {
            const applyPercent = applyByRow[plot.id] ?? null;
            const productKgHa =
              applyPercent === null
                ? null
                : showSavedAmounts
                ? applyPercent === initialData?.applyByPlot[plot.id]
                  ? initialData?.productKgHaByPlot?.[plot.id] ?? null
                  : null
                : (applyPercent / 100) * totalRate;
            const nutrientAmounts = calculateNutrientAmount(
              standardFertilisers,
              productKgHa ?? 0,
              nutrientCode,
              plot.areaHa
            );
            return {
              id: plot.id,
              label: plot.label,
              applyPercent,
              productKgHa,
              productKg:
                productKgHa === null
                  ? null
                  : calculateTotalAmount(productKgHa, plot.areaHa, "kg/ha"),
              nutrientKgHa:
                applyPercent === null || showSavedAmounts
                  ? null
                  : nutrientAmounts.perHa,
              nutrientKg:
                applyPercent === null || showSavedAmounts
                  ? null
                  : nutrientAmounts.total,
            };
          }),
        ])
      ),
    [
      groups,
      applyByRow,
      totalRate,
      standardFertilisers,
      nutrientCode,
      showSavedAmounts,
      initialData?.applyByPlot,
      initialData?.productKgHaByPlot,
    ]
  );

  const updateFertiliser = (
    fertiliserId: string,
    update: Partial<StandardFertiliser>
  ): void => {
    setFertilisers((prev) =>
      prev.map((fertiliser) =>
        fertiliser.id === fertiliserId
          ? { ...fertiliser, ...update, touched: true }
          : fertiliser
      )
    );
  };

  return {
    layout,
    groups,
    fertilisers,
    totalRate,
    requiredLevels: protocolNutrients?.number_of_intervals,
    levels:
      new Set(Object.values(applyByRow).filter((rate) => rate !== null)).size ||
      levels,
    totalPlots: allPlots.length,
    assigned,
    isFullyAssigned: isAllAssigned,
    isComplete: isAllAssigned && validation.isValid && isStandardValid,
    isDirty:
      JSON.stringify(fertilisers) !==
        JSON.stringify(
          initialData?.fertilisers ?? initialFertilisers(standardRate)
        ) ||
      JSON.stringify(applyByRow) !==
        JSON.stringify(initialData?.applyByPlot ?? {}),
    canReset:
      !isAutoAssigning &&
      (assigned > 0 ||
        fertilisers.length > 1 ||
        fertilisers.some(
          (fertiliser) =>
            Boolean(fertiliser.nutrientValue) ||
            fertiliser.rate !== null ||
            fertiliser.touched
        )),
    canAutoAssign:
      Boolean(initialData?.trialId) &&
      allPlots.length > 0 &&
      isStandardValid &&
      !isAutoAssigning,
    isAutoAssigning,
    autoAssignmentFailed,
    closeAutoAssignmentError: () => {
      setAutoAssignmentFailed(false);
    },
    rowsByGroup,
    applyByPlot: applyByRow,
    applyErrorsByPlot,
    highlightedPlotIds,
    expandedGroups,
    nutrientOptionsFor: (fertiliserId) =>
      options.filter(
        (nutrient) =>
          !fertilisers.some(
            (fertiliser) =>
              fertiliser.id !== fertiliserId &&
              (fertiliser.nutrientValue === nutrient.value ||
                fertiliser.nutrientValue === nutrient.label)
          )
      ),
    setFertiliserNutrient: (fertiliserId, value) => {
      updateFertiliser(fertiliserId, { nutrientValue: value });
    },
    setFertiliserRate: (fertiliserId, value) => {
      updateFertiliser(fertiliserId, { rate: value });
    },
    addFertiliser: () => {
      const id = `fertiliser-${nextFertiliserId.current}`;
      nextFertiliserId.current += 1;
      setFertilisers((prev) => [...prev, { id, rate: 0, touched: true }]);
    },
    removeFertiliser: (fertiliserId) => {
      setFertilisers((prev) =>
        prev.filter((fertiliser) => fertiliser.id !== fertiliserId)
      );
    },
    setApply: (rowId, value) => {
      setApplyByRow((prev) => ({ ...prev, [rowId]: value }));
    },
    toggleGroup: (groupId) => {
      setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    },
    togglePlotHighlight: (plotId) => {
      setHighlightedPlotIds((prev) =>
        prev.includes(plotId)
          ? prev.filter((id) => id !== plotId)
          : [...prev, plotId]
      );
    },
    autoAssign: async () => {
      const trialId = initialData?.trialId;
      if (
        !trialId ||
        !isStandardValid ||
        allPlots.length === 0 ||
        autoAssigning.current
      )
        return;
      autoAssigning.current = true;
      setIsAutoAssigning(true);
      try {
        const data = await requestNutrientAutoAssignment(
          trialId,
          groups,
          layout === "stripes"
        );
        const { assignments, levels: assignedLevels } = data;
        if (initialDataRef.current?.trialId !== trialId) return;
        setApplyByRow(assignments);
        setExpandedGroups(
          Object.fromEntries(groups.map((group) => [group.id, true]))
        );
        setLevels(assignedLevels);
        setAutoAssignmentFailed(false);
      } catch (error) {
        if (initialDataRef.current?.trialId !== trialId) return;
        console.error("Error auto-assigning nutrient rates:", error);
        setAutoAssignmentFailed(true);
      } finally {
        autoAssigning.current = false;
        setIsAutoAssigning(false);
      }
    },
    reset: () => {
      setFertilisers(initialFertilisers(EMPTY_STANDARD_RATE));
      setApplyByRow({});
      setExpandedGroups({});
      setLevels(0);
      setHighlightedPlotIds(
        groups.flatMap((group) => group.plots.map((plot) => plot.id))
      );
      setAutoAssignmentFailed(false);
    },
  };
};
